<script setup lang="ts">
import { AppLink } from '#components'

interface BannerData {
  File?: {
    Media?: {
      url?: string
      mime?: string
      alternativeText?: string
      width?: number
      height?: number
    }
    MediaMobile?: {
      url?: string
    }
  }
  Name?: string
  Title?: string
  Description?: string
  Link?: string
  Target?: string
}

const props = withDefaults(defineProps<{
  banner: BannerData
  lazy: boolean
  bannerCount?: number
  fullWidth?: boolean
}>(), {
  bannerCount: 1,
  fullWidth: false,
})

const hasOverlay = computed(() => {
  const hasContent = props?.banner?.Title || props?.banner?.Description
  const contentAboveImage = ['topleft', 'top', 'topright', 'left', 'center', 'right', 'bottomleft', 'bottom', 'bottomright'].includes(props?.banner?.ContentPosition)
  return hasContent && contentAboveImage
})

const widescreenImage = computed(() => {
  const columns = Math.min(props?.bannerCount, 3)
  const maxWidth = Math.round((props.fullWidth ? 2560 : 1324) / columns)
  return useXcdnImage(props?.banner?.File?.Media?.url, 'f=rs:fit:' + maxWidth)
})

const desktopImage = computed(() => {
  const columns = Math.min(props?.bannerCount, 3)
  const maxWidth = Math.round(1280 / columns)
  return useXcdnImage(props?.banner?.File?.Media?.url, 'f=rs:fit:' + maxWidth)
})

const tabletImage = computed(() => {
  const columns = Math.min(props?.bannerCount, 2)
  const maxWidth = Math.round(1024 / columns)
  return useXcdnImage(props?.banner?.File?.Media?.url, 'f=rs:fit:' + maxWidth)
})

const phabletImage = computed(() => {
  const columns = Math.min(props?.bannerCount, 2)
  const maxWidth = Math.round(768 / columns)
  return useXcdnImage(props?.banner?.File?.MediaMobile?.url || props?.banner?.File?.Media?.url, 'f=rs:fit:' + maxWidth)
})

const mobileImage = computed(() => {
  const columns = Math.min(props?.bannerCount, 2)
  const maxWidth = Math.round(640 / columns)
  return useXcdnImage(props?.banner?.File?.MediaMobile?.url || props?.banner?.File?.Media?.url, 'f=rs:fit:' + maxWidth)
})

const element = computed(() => {
  return props.banner?.File?.Link ? AppLink : 'div'
})

const desktopRatio = computed(() => {
  return props?.banner?.File?.Media?.width / props?.banner?.File?.Media?.height
})

const mobileRatio = computed(() => {
  if (props?.banner?.File?.MediaMobile?.width) {
    return props?.banner?.File?.MediaMobile?.width / props?.banner?.File?.MediaMobile?.height
  }

  return desktopRatio.value
})
</script>

<template>
  <article
    :class="[
      'banner',
      `banner--${banner?.ContentPosition}`,
      {
        'banner--overlay': hasOverlay,
      },
    ]"
    :style="{
      '--mobile-ratio': mobileRatio,
      '--desktop-ratio': desktopRatio,
    }"
  >
    <Head
      v-if="!lazy && banner?.File?.Media?.url && banner?.File?.Media?.mime?.includes('image')"
      render-priority="10"
    >
      <Link
        key="banner-widescreen"
        rel="preload"
        as="image"
        :href="widescreenImage"
        fetchpriority="high"
        media="(min-width: 1280px)"
      />
      <Link
        key="banner-desktop"
        rel="preload"
        as="image"
        :href="desktopImage"
        fetchpriority="high"
        media="(min-width: 1024px)"
      />
      <Link
        key="banner-tablet"
        rel="preload"
        as="image"
        :href="tabletImage"
        fetchpriority="high"
        media="(min-width: 768px)"
      />
      <Link
        key="banner-phablet"
        rel="preload"
        as="image"
        :href="phabletImage"
        fetchpriority="high"
        media="(min-width: 640px)"
      />
      <Link
        key="banner-mobile"
        rel="preload"
        as="image"
        :href="mobileImage"
        fetchpriority="high"
        media="(max-width: 639px)"
      />
    </Head>
    <Head
      v-if="!lazy && banner?.File?.Media?.url && banner?.File?.Media?.mime?.includes('video')"
      render-priority="10"
    >
      <Link
        rel="preload"
        as="video"
        :href="banner?.File?.Media?.url"
        fetchpriority="high"
        media="(min-width: 640px)"
      />
      <Link
        rel="preload"
        as="video"
        :href="banner?.File?.MediaMobile?.url"
        fetchpriority="high"
        media="(max-width: 639px)"
      />
    </Head>
    <div class="banner__wrapper">
      <div class="banner__container">
        <div class="banner__content">
          <h3
            v-if="banner?.Title"
            class="banner__heading"
          >
            <component
              :is="element"
              :to="banner?.File?.Link"
              class="banner__link"
            >
              {{ banner.Title }}
            </component>
          </h3>

          <p
            v-if="banner?.Description"
            class="banner__description"
          >
            {{ banner.Description }}
          </p>

          <div
            v-if="banner?.ButtonText || banner?.ButtonType === 'icon_only'"
            class="banner__button"
            :class="`banner__button--${banner?.ButtonType}`"
          >
            <span>{{ banner?.ButtonText }}</span>
            <Icon name="icon:arrow" />
          </div>
        </div>
      </div>

      <div class="banner__visual">
        <picture
          v-if="banner?.File?.Media?.url && banner?.File?.Media?.mime?.includes('image')"
          class="banner__picture"
        >
          <source
            :srcset="widescreenImage"
            media="(min-width: 1280px)"
          >
          <source
            :srcset="desktopImage"
            media="(min-width: 1024px)"
          >
          <source
            :srcset="tabletImage"
            media="(min-width: 768px)"
          >
          <source
            :srcset="phabletImage"
            media="(min-width: 640px)"
          >
          <source
            :srcset="mobileImage"
          >
          <img
            class="banner__image"
            :src="widescreenImage"
            :alt="banner?.File?.Media?.alternativeText || banner?.Name || ''"
            :width="banner?.File?.Media?.width"
            :height="banner?.File?.Media?.height"
            :loading="lazy ? 'lazy' : 'eager'"
            :fetchpriority="lazy ? 'low' : 'high'"
            :decoding="lazy ? 'async' : 'sync'"
          >
        </picture>
        <div
          v-else-if="banner?.File?.Media?.url && banner?.File?.Media?.mime?.includes('video')"
          class="banner__video"
        >
          <UiVideo
            v-if="banner?.File?.Media?.url"
            :class="{ 'video--tablet-plus': banner?.File?.Media?.url && banner?.File?.MediaMobile?.url }"
            :src="banner?.File?.Media?.url"
            :alt="banner?.File?.Media?.alternativeText || banner?.Name"
            :width="banner?.File?.Media?.width"
            :height="banner?.File?.Media?.height"
            :loading="lazy ? 'lazy' : 'eager'"
            :fetchpriority="lazy ? 'auto' : 'high'"
            :controls="false"
            autoplay
            muted
            loop
          />
          <UiVideo
            v-if="banner?.File?.MediaMobile?.url && banner?.File?.MediaMobile?.mime?.includes('video')"
            class="video--mobile-phablet"
            :src="banner?.File?.MediaMobile?.url"
            :alt="banner?.File?.MediaMobile?.alternativeText || banner?.Name"
            :width="banner?.File?.MediaMobile?.width"
            :height="banner?.File?.MediaMobile?.height"
            :loading="lazy ? 'lazy' : 'eager'"
            :fetchpriority="lazy ? 'auto' : 'high'"
            :controls="false"
            autoplay
            muted
            loop
          />
        </div>
      </div>
    </div>

    <!-- <pre>banner: {{ banner }}</pre> -->
  </article>
</template>

<style>
@import url("~layers/app/components/Strapi/Banner.css");
</style>
